import React from 'react';
import StatusLabel, { StatusType } from './status-label';
import SharedCard from './shared-card';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import type { SvgIconProps } from '@mui/material/SvgIcon';

export interface FieldItem {
  label?: string;
  value: string;
  icon?: React.ElementType<SvgIconProps>;
}

export interface MainSectionItem {
  mainTitle: string;
  description?: string;
  fields?: FieldItem[];
  status?: StatusType;
}

interface SharedReviewLayoutMainCardProps {
  section: MainSectionItem;
  isDrawer?: boolean;
}

export default function SharedReviewLayoutMainCard({
  section,
  isDrawer = false,
}: SharedReviewLayoutMainCardProps) {
  const theme = useTheme();

  const fieldRows: FieldItem[][] = [];
  if (section.fields) {
    for (let i = 0; i < section.fields.length; i += 2) {
      fieldRows.push(section.fields.slice(i, i + 2));
    }
  }

  return (
    <SharedCard key={section.mainTitle} sx={{ mb: 1 }}>
      <Box
        sx={{
          p: 2,
          pb: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexShrink: 0,
        }}
      >
        <Box pr={2}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: theme.palette.semantic.text.title2,
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'normal',
            }}
          >
            {section.mainTitle}
          </Typography>
          {section.description && (
            <Typography
              variant="subtitle2"
              sx={{
                mt: 0.5,
                color: theme.palette.semantic.text.support,
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'normal',
              }}
            >
              {' '}
              {section.description}{' '}
            </Typography>
          )}
        </Box>
        {section.status && (
          <Box sx={{ pt: 1 }}>
            <StatusLabel status={section.status} />
          </Box>
        )}
      </Box>

      <Box
        sx={{
          p: 2,
          pt: 0,
          width: '100%',
          flex: 1,
          minHeight: 0,
          overflowY: isDrawer ? 'visible' : 'auto',
        }}
      >
        <Grid container spacing={2}>
          {fieldRows.map((row, rowIndex) => (
            <Grid
              size={{ xs: 12 }}
              key={`row-${rowIndex.toString()}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              {row.map((field, fieldIndex) => {
                const FieldIcon = field.icon || undefined;
                return (
                  <React.Fragment
                    key={`${field.label ?? 'field'}-${fieldIndex.toString()}`}
                  >
                    {fieldIndex === 1 && (
                      <Typography
                        component="span"
                        variant="subtitle2"
                        sx={{
                          px: 2,
                          color: theme.palette.semantic.text.support,
                        }}
                      >
                        •
                      </Typography>
                    )}

                    <Box
                      sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      {FieldIcon && (
                        <FieldIcon
                          sx={{
                            fontSize: '14px',
                            color: theme.palette.semantic.text.support,
                          }}
                        />
                      )}
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: theme.palette.semantic.text.support,
                        }}
                      >
                        {field.label && (
                          <Box component="span" sx={{ mr: 0.5 }}>
                            {field.label}:
                          </Box>
                        )}

                        {field.value}
                      </Typography>
                    </Box>
                  </React.Fragment>
                );
              })}
            </Grid>
          ))}
        </Grid>
      </Box>
    </SharedCard>
  );
}
